/**
 * Widget session reliability telemetry.
 *
 * Emits OTLP HTTP JSON directly to SigNoz Cloud — no backend proxy. This
 * powers the Team Health Scorecard "apps: widget crash-free session rate"
 * panel (Q2 Goal 1 - Reliability).
 *
 * Design notes:
 *   - Kept separate from the Segment analytics pipeline (different purpose:
 *     operational reliability vs product-usage analytics).
 *   - Two counters emitted: `apps.widget.session_started` on mount,
 *     `apps.widget.session_crashed` on window.error, unhandledrejection,
 *     or React render crash via the ErrorBoundary.
 *   - Crash counter is de-duplicated per session (one per session max) so a
 *     cascade of errors on the same page doesn't over-count.
 *   - Error DETAIL (message + stack) is emitted as OTLP log records via
 *     `recordError`, capped per session. Restricted to the platforms in
 *     ERROR_LOG_PLATFORMS while we validate volume and attribute hygiene.
 *   - No SDK deps: direct OTLP/HTTP JSON POST. Smaller bundle than OTEL JS.
 *
 * Required env vars:
 *   VITE_SIGNOZ_OTLP_ENDPOINT   — e.g. https://ingest.eu.signoz.cloud:443
 *   VITE_SIGNOZ_INGESTION_KEY   — write-only ingestion key with per-signal
 *                                 daily limits (metrics AND logs)
 *
 * When either is unset, telemetry no-ops silently — safe for local dev.
 */

// Read env lazily so build-time inlining and test-time vi.stubEnv both work.
function readConfig(): {
  endpoint: string | undefined;
  key: string | undefined;
  buildVersion: string;
  environment: string;
} {
  return {
    endpoint: import.meta.env["VITE_SIGNOZ_OTLP_ENDPOINT"] as string | undefined,
    key: import.meta.env["VITE_SIGNOZ_INGESTION_KEY"] as string | undefined,
    buildVersion: (import.meta.env["VITE_BUILD_VERSION"] as string | undefined) ?? "local",
    environment: (import.meta.env["VITE_ENVIRONMENT"] as string | undefined) ?? (import.meta.env.PROD ? "prod" : "dev"),
  };
}

// OTLP AggregationTemporality.CUMULATIVE = 2 (monotonic counter, total since start)
const AGG_TEMPORALITY_CUMULATIVE = 2;

// OTLP SeverityNumber.ERROR = 17
const SEVERITY_NUMBER_ERROR = 17;

// Staged rollout: web first (our own origin, no add-in CSP involved); widen
// once volume and attribute hygiene are confirmed in SigNoz.
const ERROR_LOG_PLATFORMS: ReadonlySet<string> = new Set(["web"]);

// Healthy sessions produce 0-2 distinct errors; 50 bounds a pathological
// session at ~250KB against the ingestion key's daily limit.
const MAX_DISTINCT_ERRORS_PER_SESSION = 50;
const MAX_ERROR_MESSAGE_LENGTH = 500;
const MAX_ERROR_STACK_LENGTH = 4000;

export type ErrorKind = "error" | "unhandledrejection" | "react" | "handled";

export interface SessionLabels {
  tenant: string;
  platform: string; // host: outlook | salesforce | web | ...
}

interface OtlpAttribute {
  key: string;
  value: { stringValue: string };
}

interface OtlpDataPoint {
  attributes: OtlpAttribute[];
  startTimeUnixNano: string;
  timeUnixNano: string;
  asInt: string;
}

interface OtlpMetric {
  name: string;
  unit: string;
  sum: {
    dataPoints: OtlpDataPoint[];
    aggregationTemporality: number;
    isMonotonic: boolean;
  };
}

function nowUnixNano(): string {
  // Nanoseconds since epoch as a string. Use BigInt(..) call form (not the
  // 1_000_000n literal) to stay compatible with TS targets below ES2020.
  return (BigInt(Date.now()) * BigInt(1_000_000)).toString();
}

function buildAttrs(labels: SessionLabels, extra: Record<string, string> = {}): OtlpAttribute[] {
  return Object.entries({ ...labels, ...extra }).map(([key, value]) => ({
    key,
    value: { stringValue: String(value) },
  }));
}

function buildOtlpPayload(
  metricName: string,
  labels: SessionLabels,
  extra: Record<string, string>,
  startTimeUnixNano: string,
): object {
  const { buildVersion, environment } = readConfig();
  const ts = nowUnixNano();
  const metric: OtlpMetric = {
    name: metricName,
    unit: "1",
    sum: {
      dataPoints: [
        {
          attributes: buildAttrs(labels, extra),
          startTimeUnixNano,
          timeUnixNano: ts,
          asInt: "1",
        },
      ],
      aggregationTemporality: AGG_TEMPORALITY_CUMULATIVE,
      isMonotonic: true,
    },
  };

  return {
    resourceMetrics: [
      {
        resource: {
          attributes: [
            { key: "service.name", value: { stringValue: "widget" } },
            { key: "service.version", value: { stringValue: buildVersion } },
            { key: "deployment.environment", value: { stringValue: environment } },
          ],
        },
        scopeMetrics: [
          {
            scope: { name: "apps.widget", version: buildVersion },
            metrics: [metric],
          },
        ],
      },
    ],
  };
}

function buildOtlpLogPayload(message: string, labels: SessionLabels, extra: Record<string, string>): object {
  const { buildVersion, environment } = readConfig();
  return {
    resourceLogs: [
      {
        resource: {
          attributes: [
            { key: "service.name", value: { stringValue: "widget" } },
            { key: "service.version", value: { stringValue: buildVersion } },
            { key: "deployment.environment", value: { stringValue: environment } },
          ],
        },
        scopeLogs: [
          {
            scope: { name: "apps.widget", version: buildVersion },
            logRecords: [
              {
                timeUnixNano: nowUnixNano(),
                severityNumber: SEVERITY_NUMBER_ERROR,
                severityText: "ERROR",
                body: { stringValue: message },
                attributes: buildAttrs(labels, extra),
              },
            ],
          },
        ],
      },
    ],
  };
}

function normalizeError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: error.message.slice(0, MAX_ERROR_MESSAGE_LENGTH),
      stack: error.stack?.slice(0, MAX_ERROR_STACK_LENGTH),
    };
  }
  return { message: String(error).slice(0, MAX_ERROR_MESSAGE_LENGTH) };
}

async function postOtlp(path: "/v1/metrics" | "/v1/logs", payload: object): Promise<void> {
  const { endpoint, key } = readConfig();
  if (!endpoint || !key) {
    // Local dev / missing config — silently no-op. Logging once is handled
    // by the provider, so we don't spam per-event here.
    return;
  }
  try {
    await fetch(`${endpoint.replace(/\/$/, "")}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "signoz-ingestion-key": key,
      },
      body: JSON.stringify(payload),
      // keepalive lets the request survive page unload, important for crash
      // events fired just before the widget/tab closes.
      keepalive: true,
    });
  } catch {
    // Telemetry must never take down the widget. Swallow network errors.
  }
}

/**
 * Session-scoped telemetry client. Build one per widget mount.
 */
export class SessionTelemetry {
  private readonly sessionStartUnixNano: string;
  private crashedOnce = false;
  private readonly seenErrors = new Set<string>();
  private countedAuthenticated = false;
  private userEmail: string | undefined;

  constructor(private readonly labels: SessionLabels) {
    this.sessionStartUnixNano = nowUnixNano();
  }

  /**
   * Update the tenant label in place once auth resolves. Earlier records
   * are not re-emitted or re-attributed.
   */
  setTenant(tenant: string): void {
    this.labels.tenant = tenant;
  }

  /**
   * Attach the signed-in user to all subsequent records (counters and error
   * logs). Aggregate queries just ignore the label; per-user views (crash
   * offenders, old-widget holdouts) filter on it. Same exposure policy as
   * the employee mail the backend's structured logs already carry.
   */
  setUser(email: string | undefined): void {
    this.userEmail = email;
  }

  private userAttr(): Record<string, string> {
    return this.userEmail ? { user: this.userEmail } : {};
  }

  async recordSessionStart(): Promise<void> {
    await postOtlp(
      "/v1/metrics",
      buildOtlpPayload("apps.widget.session_started", this.labels, this.userAttr(), this.sessionStartUnixNano),
    );
  }

  /**
   * Count the session once against its resolved tenant.
   *
   * Skipped while the tenant is unresolved: the pre-auth instance would emit
   * a tenant=unknown datapoint and the post-auth instance a second one,
   * double-counting the session. The post-auth re-sync records it once.
   */
  async recordSessionAuthenticated(): Promise<void> {
    if (this.labels.tenant === "unknown") return;
    if (this.countedAuthenticated) return;
    this.countedAuthenticated = true;
    await postOtlp(
      "/v1/metrics",
      buildOtlpPayload(
        "apps.widget.session_variant_activated",
        this.labels,
        this.userAttr(),
        this.sessionStartUnixNano,
      ),
    );
  }

  async recordSessionCrash(kind: "error" | "unhandledrejection" | "react"): Promise<void> {
    if (this.crashedOnce) return;
    this.crashedOnce = true;
    await postOtlp(
      "/v1/metrics",
      buildOtlpPayload(
        "apps.widget.session_crashed",
        this.labels,
        { kind, ...this.userAttr() },
        this.sessionStartUnixNano,
      ),
    );
  }

  /**
   * Ship one error with message + stack as an OTLP log record.
   *
   * Each distinct (kind, message) pair ships once per session; repeats are
   * suppressed. Restricted to ERROR_LOG_PLATFORMS.
   *
   * `extra` takes intentional fields, never dumped objects: attributes ship
   * unredacted, so keep auth tokens and signed URLs out. Customer identifiers
   * and values follow the same policy as the api's structured logs.
   */
  async recordError(kind: ErrorKind, error: unknown, extra: Record<string, string> = {}): Promise<void> {
    if (!ERROR_LOG_PLATFORMS.has(this.labels.platform)) return;
    if (this.seenErrors.size >= MAX_DISTINCT_ERRORS_PER_SESSION) return;

    const { message, stack } = normalizeError(error);
    const fingerprint = `${kind}:${message}`;
    if (this.seenErrors.has(fingerprint)) return;
    this.seenErrors.add(fingerprint);

    await postOtlp(
      "/v1/logs",
      buildOtlpLogPayload(message, this.labels, {
        kind,
        ...(stack ? { stack } : {}),
        ...this.userAttr(),
        ...extra,
      }),
    );
  }

  /** Test-only: inspect de-dup state. */
  get hasCrashed(): boolean {
    return this.crashedOnce;
  }
}

export function isTelemetryConfigured(): boolean {
  const { endpoint, key } = readConfig();
  return Boolean(endpoint && key);
}

// Exported for test injection
export const __internals = { buildOtlpPayload, buildOtlpLogPayload };
